'use client'

import { useState } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { flagshipAccentAt, FLAGSHIP_ACCENT_VAR } from '@/lib/flagship-colors'

interface Faq {
  question: string
  answer: string
}

// Visible FAQ; the FAQPage JSON-LD in app/[locale]/page.tsx mirrors these same messages.
export default function HomeFaq() {
  const t = useTranslations('homepage.techDetail')
  const faqs = t.raw('faqs') as Faq[]
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <section
      aria-labelledby="faq-heading"
      id="faq"
      className="home-snap px-4 py-6 sm:px-10 sm:py-10 lg:px-16"
    >
      <div
        className="relative mx-auto w-full max-w-3xl overflow-hidden rounded-2xl bg-[rgba(24,18,52,0.55)] px-5 py-6 sm:px-8 sm:py-8 md:max-w-4xl lg:px-10 lg:py-10"
        style={{
          boxShadow:
            'inset 0 0 0 1px rgba(255,255,255,0.25), inset 0 1.5px 0 0 rgba(255,255,255,0.35), inset 0 0 60px -32px var(--flagship-pink), 0 20px 60px -24px rgba(0,0,0,0.5)',
        }}
      >
        {/* specular top sheen */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/[0.10] to-transparent" />
        {/* soft accent glow bleeding in from the corners */}
        <div
          className="pointer-events-none absolute -inset-px opacity-60"
          style={{
            background:
              'radial-gradient(80% 60% at 0% 0%, color-mix(in srgb, var(--flagship-cyan) 16%, transparent), transparent 60%), radial-gradient(80% 60% at 100% 100%, color-mix(in srgb, var(--flagship-pink) 16%, transparent), transparent 60%)',
          }}
        />

        <div className="relative mx-auto max-w-3xl">
          <h2
            tabIndex={0}
            id="faq-heading"
            className="block-focus text-center [font-family:var(--font-display)] text-2xl text-[var(--flagship-text)] sm:text-4xl"
          >
            {t('faqHeading')}
          </h2>
          <div className="mt-4 divide-y divide-white/30 border-t border-white/30 sm:mt-4">
            {faqs.map((faq, index) => {
              const accent = FLAGSHIP_ACCENT_VAR[flagshipAccentAt(index)]
              return (
                <div key={faq.question}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="flex w-full items-center justify-between gap-3 py-2.5 text-left sm:py-4"
                    aria-expanded={openFaq === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="text-base font-medium text-[var(--flagship-text)]">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 transition-transform ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                      style={{ color: accent }}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === index && (
                      <m.div
                        id={`faq-answer-${index}`}
                        tabIndex={0}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="block-focus overflow-hidden"
                      >
                        <p className="mb-0 pb-3 text-base text-[var(--flagship-text)]/90 sm:pb-4">
                          {faq.answer}
                        </p>
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
